import {
  ChevronDownIcon,
  DeleteIcon,
  DownloadIcon,
  ErrorIcon,
  LogsIcon,
  StopIcon,
} from '@assets/icons';
import { IconButton } from '@components/button';
import { Group } from '@components/layout';
import { ExternalLink } from '@components/link';
import { useConfirmation } from '@components/modal';
import { ProgressBar } from '@components/progress';
import { Typography } from '@components/typography';
import {
  DatasetJob,
  useDeleteDatasetMutation,
  useDownloadDatasetMutation,
  useEditDatasetMutation,
} from '@generated/graphql';
import { downloadFromUrl } from '@helper-functions/save-file';
import { Grid, Stack, useTheme } from '@mui/material';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import { useNotifications } from '@notifications/Notifications';
import { getJobCompletionPercentage } from '@utils/jobs';
import { useMemo } from 'react';

type JobsManagerJobPreviewInfoProps = DatasetJob & {
  expand: () => void;
  expanded: boolean;
  refetch: VoidFunction;
};

export const JobsManagerJobPreviewInfo = ({
  channel,
  createdAt,
  datasetId,
  estimatedEndAt,
  expand,
  expanded,
  name,
  runs,
  runsCancelled,
  runsFailed,
  runsSuccess,
  runsTimeout,
  status,
  workspaceId,
  refetch,
}: JobsManagerJobPreviewInfoProps) => {
  const { palette } = useTheme();
  const { organizationId } = useOrganizationContext();
  const { createAsyncNotification } = useNotifications();
  const { showConfirmation } = useConfirmation();
  const [downloadDatasetMutation] = useDownloadDatasetMutation();
  const [deleteDataset] = useDeleteDatasetMutation();
  const [hideDataset] = useEditDatasetMutation();

  const handleFileDownloadClick = createAsyncNotification(
    'Download started - check your browser downloads',
    async () => {
      const response = await downloadDatasetMutation({
        variables: {
          datasetId,
          workspaceId,
        },
      });

      await downloadFromUrl({
        url: response?.data?.downloadDataset,
        name: `${name || 'Untitled'}.zip`,
      });
    },
    { loadingMessage: 'Starting download...' },
  );

  const handleDatasetDeleteClick = () => {
    showConfirmation({
      message: 'Are you sure to clear the job?',
      affirmText: 'Yes, clear',
      onAffirm: createAsyncNotification('Successfully cleared job.', async () => {
        if (['success', 'failed', 'timeout', 'cancelled', 'partially_success'].includes(status)) {
          await hideDataset({ variables: { workspaceId, datasetId, showJob: false } });
        } else {
          await deleteDataset({
            variables: {
              datasetId,
              workspaceId,
            },
          });
        }
        refetch();
      }),
    });
  };

  const handleJobStopClick = () => {
    showConfirmation({
      message: 'Are you sure to stop the job?',
      affirmText: 'Yes, stop',
      onAffirm: createAsyncNotification('Successfully stopped the job.', async () => {
        await deleteDataset({
          variables: {
            datasetId,
            workspaceId,
          },
        });
        refetch();
      }),
    });
  };

  const percent = useMemo(() => {
    return getJobCompletionPercentage(status, estimatedEndAt, createdAt);
  }, [status, createdAt, estimatedEndAt]);

  const jobsDoneCount = runsSuccess + runsCancelled + runsFailed + runsTimeout;

  return (
    <Grid container>
      <Grid item md={3} xs={6}>
        <Stack>
          <Typography handleOverFlow variant="body2" color={palette.text.contrast}>
            {name || '-'}
          </Typography>
          <Typography variant="body2" style={{ color: palette.text.light }}>
            {channel || 'unknown'}
          </Typography>
        </Stack>
      </Grid>
      <Grid item md={3} xs={6} sx={{ paddingLeft: '10px' }}>
        <ProgressBar
          percent={status === 'queued' ? 0 : percent || 0}
          error={status === 'failed'}
          warning={status === 'timeout'}
          sx={{ marginTop: '3px', marginBottom: '2px' }}
        />
      </Grid>
      <Grid
        item
        xs={6}
        md={3}
        sx={{ paddingLeft: { xs: 0, md: '30px' }, alignSelf: { xs: 'center', md: 'flex-start' } }}
      >
        <Stack>
          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
            {status || '-'}
          </Typography>
          <Typography variant="body2">
            {jobsDoneCount || '0'} of {runs || '0'} runs
          </Typography>
        </Stack>
      </Grid>
      <Grid item xs={6} md={3} sx={{ paddingLeft: '10px', minWidth: '128px' }}>
        <Group
          sx={{
            paddingLeft: '10px',
            justifyContent: 'flex-end',
            alignItems: 'center',
            height: '100%',
            alignSelf: 'center',
            gap: 4,
          }}
        >
          {percent === 100 && status === 'failed' && (
            <IconButton
              Icon={ErrorIcon}
              variant="error"
              size={20}
              onClick={() => {
                if (typeof window !== 'undefined') {
                  window.open(
                    `/${organizationId}/workspaces/${workspaceId}/jobs/${datasetId}/logs`,
                    '_blank',
                  );
                }
              }}
            />
          )}
          {percent === 100 &&
            (status === 'success' ||
              status === 'partially_success' ||
              status === 'complete' ||
              status === 'partially_complete') && (
              <IconButton
                onClick={handleFileDownloadClick}
                tooltip="Download job"
                Icon={DownloadIcon}
                size={20}
              />
            )}
          <ExternalLink
            unstyled
            href={`/${organizationId}/workspaces/${workspaceId}/jobs/${datasetId}/logs`}
          >
            <IconButton tooltip="View logs" Icon={LogsIcon} size={20} />
          </ExternalLink>
          {status === 'running' && jobsDoneCount >= 1 ? (
            <IconButton
              onClick={handleJobStopClick}
              data-cy="Jobs-Manager-Job-Preview-Info-Stop-Icon"
              tooltip="Stop job"
              Icon={StopIcon}
              size={20}
            />
          ) : (
            <IconButton
              onClick={handleDatasetDeleteClick}
              tooltip="Clear job"
              Icon={DeleteIcon}
              size={20}
              data-cy="Jobs-Manager-Job-Preview-Info-Delete-Icon"
            />
          )}
          <IconButton
            onClick={expand}
            Icon={ChevronDownIcon}
            size={20}
            data-cy={
              !expanded
                ? 'Jobs-Manager-Job-Preview-Info-Expand-View-Down-Icon'
                : 'Jobs-Manager-Job-Preview-Info-Expand-View-Up-Icon'
            }
            style={{
              transition: 'all 0.2s',
              transform: !expanded ? 'rotate(0deg)' : 'rotate(180deg)',
            }}
          />
        </Group>
      </Grid>
    </Grid>
  );
};
